import { useMemo } from 'react';
import styled from 'styled-components';

import { StyledGapContainer } from '@views/auth/signup/styles';

type JobPostDescriptionProps = {
  hasDescription: boolean;
  description: string;
  unionFittingRateNote: string;
  unionInterviewRateNote: string;
  nonUnionFittingRateNote: string;
  nonUnionInterviewRateNote: string;
  hasRateNotes: boolean;
};
export const JobPostDescription = ({
  hasDescription,
  hasRateNotes,
  description,
  unionFittingRateNote,
  unionInterviewRateNote,
  nonUnionFittingRateNote,
  nonUnionInterviewRateNote,
}: JobPostDescriptionProps) => {
  const mainContainerGap = useMemo(
    () => (hasDescription ? 8 : 12),
    [hasDescription]
  );

  const rateNotes = useMemo(() => {
    return [
      {
        label: 'Union Fitting Rate',
        value: unionFittingRateNote,
        key: 'union_fitting_rate',
        labelWidth: 122,
        labelWidthMobile: 140,
      },
      {
        label: 'Union Interview Rate',
        value: unionInterviewRateNote,
        key: 'union_interview_rate',
        labelWidth: 142,
        labelWidthMobile: 162,
      },
      {
        label: 'Non-Union Fitting Rate',
        value: nonUnionFittingRateNote,
        key: 'non_union_fitting_rate',
        labelWidth: 156,
        labelWidthMobile: 178,
      },
      {
        label: 'Non-Union Interview Rate',
        value: nonUnionInterviewRateNote,
        key: 'non_union_interview_rate',
        labelWidth: 175,
        labelWidthMobile: 200,
      },
    ].filter((item) => !!item?.value?.trim());
  }, [
    unionFittingRateNote,
    unionInterviewRateNote,
    nonUnionFittingRateNote,
    nonUnionInterviewRateNote,
  ]);

  return (
    <StyledContainer gap={mainContainerGap}>
      <div className='font-weight-600'>Description</div>

      <StyledGapContainer gap={12}>
        {hasDescription && (
          <div className='job-description-text text-wrap-anywhere'>
            {description}
          </div>
        )}
        {hasDescription && hasRateNotes && (
          <div className='horizontal-divider' />
        )}
        {hasRateNotes && (
          <StyledGapContainer gap={4}>
            {rateNotes.map((item) => (
              <div key={item?.key} className={`rate-note-item`}>
                <StyledRateNoteItemLabel
                  width={item?.labelWidth}
                  widthMobile={item?.labelWidthMobile}
                >
                  {item?.label}:
                </StyledRateNoteItemLabel>
                <div className='rate-note-item-value'>{item?.value}</div>
              </div>
            ))}
          </StyledGapContainer>
        )}
      </StyledGapContainer>
    </StyledContainer>
  );
};

type StyledContainerProps = { gap: number };
const StyledContainer = styled.div<StyledContainerProps>`
  display: flex;
  flex-direction: column;
  gap: ${({ gap }) => `${gap}px`};
  font-size: 14px;
  line-height: 20px;

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 24px;
  }

  .job-description-text {
    white-space: pre-wrap;
  }

  .horizontal-divider {
    width: 100%;
    height: 1px;
    background-color: ${({ theme }) => theme.palette.stroke[0]};
  }

  .rate-note-item {
    display: flex;
    gap: 4px;

    .rate-note-item-value {
      white-space: pre-wrap;
      overflow-wrap: anywhere;
      word-break: break-word;
    }
  }
`;

type StyledRateNoteItemLabelProps = {
  width: number;
  widthMobile: number;
};
const StyledRateNoteItemLabel = styled.div<StyledRateNoteItemLabelProps>`
  color: ${({ theme }) => theme.palette.text[9]};
  flex-shrink: 0;
  width: ${({ width }) => `${width}px`};
  @media (max-width: 768px) {
    width: ${({ widthMobile }) => `${widthMobile}px`};
  }
`;
